import {
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    LinearProgress,
    Stack,
    Typography,
    useTheme
} from "@mui/joy"
import React, {useEffect, useMemo, useRef, useState} from "react"
import {
    Circle, EarIcon, Mic2, PlayIcon, SpeakerIcon, User2, UserRound,
} from "lucide-react"
import {AnimatePresence, motion} from "motion/react"
import {useTestRoom} from "../../../../../hooks/useTestRoom"
import {useFeedback} from "../../../../../hooks/useFeedback";
import {UploadTask} from "firebase/storage";
import {SpeakingRecording} from "../../../../../models/submission";
import {Question, QuestionAttempt, QuestionGroup} from "../../../../../models/question";
import {storage} from "../../../../../firebase";
import {ref, getDownloadURL, uploadBytesResumable, uploadBytes} from "firebase/storage"
import LoadingPageComponentSmall from "../../../../Loading/LoadingPageComponentSmall";
import {useCountUp} from "use-count-up";
import {useTranslation} from "react-i18next";
import LoadingError from "../../../../Error/LoadingError";

type SpeakingQuestionComponentProps = {
    questions: Question[],
    onAudioReady?: (audio: SubmissionReadyAudio) => void,
    audioContent: {
        [v in "questions" | "instructions"]: string[]
    }
}

type SubmissionReadyAudio = {
    file?: File,
    fileExtension?: string,
    blob?: Blob,
    url?: string,
    name?: string,
    size?: number,
    duration?: number,
    timeStamp?: string
}


const IeltsSpeakingQuestionComponent = (props: SpeakingQuestionComponentProps) => {
    const feedback = useFeedback()
    const theme = useTheme()
    const testRoom = useTestRoom()
    const {t} = useTranslation()
    const [isRecordingSync, setIsRecordingSync] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [uploadSuccess, setUploadSuccess] = useState(true)
    const [recordingTime, setRecordingTime] = useState(0)
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1)
    const [instructionIndex, setInstructionIndex] = useState(0)
    const [pitchLevel, setPitchLevel] = useState<number>()
    const [isOver, setIsOver] = useState(false)
    const [part2Countdown, setPart2Countdown] = useState<number>(null)
    const part2CountdownRef = useRef<number>(null)
    const part2CountdownIntervalRef = useRef(null)
    const inactivityTimer = useRef(null)
    const silenceRafRef = useRef(null)
    const INACTIVITY_DELAY = 5000

    const currentQuestionIndexRef = useRef(null)
    const instructionIndexRef = useRef(null)
    const isOverRef = useRef(null)
    const userResponsesRef = useRef<SpeakingRecording[]>([])
    const currentPlayerRef = useRef<HTMLAudioElement>(null)
    const isRecordingRef = useRef(false)
    const mediaRecorderRef = useRef(null)
    const audioChunksRef = useRef([])
    const analyserRef = useRef(null)
    const audioCtxRef = useRef(null)
    const timerRef = useRef(null)
    const recordingTimeRef = useRef(0)
    const maxResponseDurationRef = useRef<number>(null)
    const streamRef = useRef(null)
    const intensity = Math.min(pitchLevel / 60, 1)
    const {value: maxAllowedTimeProgressValue} = useCountUp({
        isCounting: true,
        duration: INACTIVITY_DELAY / 1000,
        easing: 'linear',
        start: 0,
        end: 100,
        onComplete: () => ({
            shouldRepeat: true,
            delay: 0,
        }),
    })


    const getSupportedMimeType = () => {
        const types = [
            'audio/webm',
            'audio/mp4',
            'audio/ogg',
            'audio/wav'
        ]

        for (let type of types)
            if (MediaRecorder.isTypeSupported(type))
                return type
        return ''
    }

    const getExtensionFromMimeType = (mimeType) => {
        if (!mimeType) return 'webm'

        const baseType = mimeType.split(';')[0].trim()

        const extensions: Record<string, string> = {
            'audio/webm': 'webm',
            'audio/ogg': 'ogg',
            'audio/mp4': 'mp4',
            'audio/mpeg': 'mp3',
            'audio/wav': 'wav',
            'audio/x-wav': 'wav',
            'audio/flac': 'flac',
            'audio/aac': 'aac'
        }

        return extensions[baseType] || 'webm'
    }

    const startRecording = async (recordDuration: number, disableSilence?: boolean) => {
        try {
            console.log("[iOS] startRecording called, AudioContext state:", audioCtxRef.current?.state)

            if (audioCtxRef.current?.state === "suspended")
                await audioCtxRef.current.resume()

            const stream = await navigator.mediaDevices.getUserMedia({audio: true})
            streamRef.current = stream
            connectAnalyser()
            const dataArray = new Uint8Array(analyserRef.current?.fftSize)

            const checkSilence = () => {
                analyserRef.current?.getByteTimeDomainData(dataArray) // ← fetch first

                const pitch = dataArray.reduce((sum, v) => sum + Math.abs(v - 128), 0) / dataArray.length
                setPitchLevel(pitch)

                const isSilent = pitch < 5
                if (!isSilent)
                    resetInactivityTimer()

                silenceRafRef.current = requestAnimationFrame(checkSilence)
            }

            if (!disableSilence) {
                resetInactivityTimer()
                checkSilence()
            }

            const options = getSupportedMimeType() ? {mimeType: getSupportedMimeType()} : {}
            const mediaRecorder = new MediaRecorder(stream, options)

            mediaRecorderRef.current = mediaRecorder
            audioChunksRef.current = []

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0)
                    audioChunksRef.current.push(event.data)
            }

            mediaRecorder.onstop = () => {

                // Get the mime type from the recorder
                const originalMimeType = mediaRecorder.mimeType || 'audio/webm';

                // Extract base mime type (remove codecs part)
                // Split by semicolon and take the first part
                const baseMimeType = originalMimeType.split(';')[0]

                // Get extension from mime type
                const extension = getExtensionFromMimeType(baseMimeType) ||
                    (baseMimeType.includes('webm') ? 'webm' :
                        baseMimeType.includes('mp4') ? 'mp4' :
                            baseMimeType.includes('ogg') ? 'ogg' : 'wav')


                userResponsesRef.current.push({
                    assetName: `speaking_test_${props.questions[currentQuestionIndex]?.id}_part_${props.questions[currentQuestionIndex]?.subSectionNumber}_question_${currentQuestionIndex}_submission_${testRoom.sessionId}.${extension}`,
                    fileExtension: extension,
                    questionIndex: currentQuestionIndex,
                    blob: new Blob(audioChunksRef.current),
                    durationSecs: recordingTime,
                    createdAt: new Date().getTime(),
                    subsectionNumber: props.questions[currentQuestionIndex]?.subSectionNumber,
                })
                testRoom.setAnsweredQuestions(userResponsesRef.current?.length)
            }

            mediaRecorder.start(1000)
            setIsRecordingSync(true)
            isRecordingRef.current = true

            maxResponseDurationRef.current = recordDuration
            startTimer()

        } catch (error) {
            console.error('Error accessing microphone:', error)
        }
    }

    const resetInactivityTimer = () => {
        clearTimeout(inactivityTimer.current)
        inactivityTimer.current = setTimeout(() => {
            stopRecording()
        }, INACTIVITY_DELAY)
    }

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecordingRef.current) {
            mediaRecorderRef.current.stop()
            setIsRecordingSync(false)
            clearTimer()
            cancelAnimationFrame(silenceRafRef.current)
            clearTimeout(inactivityTimer.current)
            isRecordingRef.current = false
            setInstructionIndex(0)

            if (currentQuestionIndex === props.audioContent.questions.length - 1) {
                if (testRoom.canGoToNextSubsection) {
                    testRoom.nextSubsection()
                    setCurrentQuestionIndex(-1)
                } else
                    setIsOver(true)
            } else
                setCurrentQuestionIndex(prevState => prevState + 1)

            if (streamRef.current)
                streamRef.current.getTracks().forEach(track => track.stop())
        }
    }

    const startTimer = () => {
        recordingTimeRef.current = 0
        setRecordingTime(0)
        timerRef.current = setInterval(() => {
            if (recordingTimeRef.current >= maxResponseDurationRef.current)
                stopRecording()
            else {
                setRecordingTime(prev => prev + 1)
                recordingTimeRef.current += 1
            }
        }, 1000)
    }

    const clearTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
        }
    }

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }

    const handleUploadUserResponses = async () => {
        if (uploading)
            return

        if (!userResponsesRef.current || userResponsesRef.current.length === 0)
            return feedback.showFeedback("Audio is empty", "danger")

        const qAs: QuestionAttempt[] = []
        setUploading(true)
        try {
            for (let i = 0; i < props.questions.length; i++) {
                const question = props.questions[i]
                const userResponse = userResponsesRef.current[i]

                const storageRef = ref(storage, `audios/${userResponse.assetName}`)
                const cleanBlob = new Blob([userResponse.blob], {
                    type: userResponse.blob.type.split(";")[0] || "audio/webm",
                })

                const uploadResult = await uploadBytes(storageRef, cleanBlob)
                const url = await getDownloadURL(uploadResult.ref)
                const cleanUserResponse = {
                    ...userResponse,
                    blob: null,
                    audioUrl: url
                }

                const questionAttempt: QuestionAttempt = {
                    questionId: question.id,
                    questionSnapshot: {
                        content: question.content,
                        correctAnswer: question.correctAnswer,
                        sectionName: question.sectionName,
                        type: question.type,
                        answerType: question.answerType,
                        testId: question.testId,
                        contentType: question.contentType,
                    },
                    userAnswer: cleanUserResponse,
                    answeredAt: new Date().getTime()
                }
                qAs.push(questionAttempt)
            }

            setUploadSuccess(true)
            setUploading(false)

        } catch (e) {
            setUploadSuccess(false)
            feedback.showFeedback(t("defaultLoadingErrorMessage"), "danger")
            setUploading(false)
            return
        }

        testRoom.submit(qAs)
    }

    const waitForReply = () => {
        if (testRoom.currentSubsectionNumber === 2)
            startRecording(120)
        else
            startRecording(25)
    }

    const connectAnalyser = () => {
        console.log("[iOS] connectAnalyser called, ctx state:", audioCtxRef.current?.state)

        if (!audioCtxRef.current || audioCtxRef.current.state === 'closed')
            audioCtxRef.current = new AudioContext()

        if (analyserRef.current) {
            analyserRef.current.disconnect()
            analyserRef.current = null
        }

        analyserRef.current = audioCtxRef.current.createAnalyser()
        const source = audioCtxRef.current.createMediaStreamSource(streamRef.current)
        source.connect(analyserRef.current)
    }

    useEffect(() => {
        if (isOver)
            handleUploadUserResponses()
    }, [isOver])

    useEffect(() => {
        currentQuestionIndexRef.current = currentQuestionIndex
    }, [currentQuestionIndex])
    useEffect(() => {
        instructionIndexRef.current = instructionIndex
    }, [instructionIndex])
    useEffect(() => {
        isOverRef.current = isOver
    }, [isOver])

    useEffect(() => {
        if (!currentPlayerRef.current) return

        const src = isOver
            ? props.audioContent.instructions['closing']?.[0]
            : (currentQuestionIndex < 0 || instructionIndex > 0)
                ? props.audioContent.instructions?.[instructionIndex]
                : props.audioContent.questions[currentQuestionIndex]

        if (!src || currentPlayerRef.current.src === src) return
        currentPlayerRef.current.src = src
        currentPlayerRef.current.load()
        currentPlayerRef.current.play().catch(console.error)
    }, [isOver, currentQuestionIndex, instructionIndex])


    return <Stack sx={{backgroundColor: "transparent"}}>

        {<audio ref={currentPlayerRef}
                style={{display: "none"}}
                onEnded={() => {


                    if (currentQuestionIndexRef.current < 0) {
                        cancelAnimationFrame(silenceRafRef.current)
                        setCurrentQuestionIndex(0)
                    } else {
                        cancelAnimationFrame(silenceRafRef.current)
                        if (testRoom.currentSubsectionNumber === 2 && instructionIndexRef.current === 0) {
                            part2CountdownRef.current = 60
                            setPart2Countdown(60)

                            part2CountdownIntervalRef.current = setInterval(() => {
                                part2CountdownRef.current -= 1
                                setPart2Countdown(part2CountdownRef.current)
                            }, 1000)

                            setTimeout(() => {
                                clearInterval(part2CountdownIntervalRef.current)
                                setPart2Countdown(0)
                                setInstructionIndex(1)
                            }, 60000)
                        } else
                            waitForReply()
                    }
                }}
        />}

        <AnimatePresence mode="wait">
            <motion.div
                key={`shadow-examiner`}
                initial={{opacity: 0, scale: 0.8, y: 20}}
                animate={{
                    opacity: 0.3,
                    backgroundColor: 'red',
                    scale: 1, y: 0
                }}
                exit={{opacity: 0, scale: 0.8, y: 20}}
                transition={{duration: 0.5}}
                className="absolute inset-0 bg-black blur-2xl rounded-2xl"
                style={{
                    top: '20px',
                    zIndex: -1
                }}
            />
            <motion.div
                key={isRecordingSync ? 'student' : 'examiner'}
                initial={{
                    rotateY: -90,
                    opacity: 0,
                    scale: 0.8,
                    z: -100,
                    backgroundColor: 'red',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
                animate={{
                    rotateY: 0,
                    opacity: 1,
                    scale: 1,
                    z: 0,
                    backgroundColor: 'transparent',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15), 0 10px 20px rgba(0, 0, 0, 0.1)'
                }}
                exit={{
                    rotateY: 90,
                    opacity: 0,
                    scale: 0.8,
                    z: -100,
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
                transition={{
                    duration: 0.5,
                    ease: [0.34, 1.56, 0.64, 1],
                    scale: {
                        duration: 0.5,
                        ease: [0.25, 0.46, 0.45, 0.94]
                    },
                    boxShadow: {
                        duration: 0.5,
                        ease: 'easeInOut'
                    }
                }}
                style={{
                    transformStyle: 'preserve-3d',
                    backfaceVisibility: 'hidden'
                }}>

                {uploading ?
                    <LoadingPageComponentSmall loadingText={t("speakingTestUploadLabel")} color={"primary"}/> :
                    (isOver && !uploadSuccess) ?
                        <LoadingError abstractText={t("speakingTestUploadFailedMessage")} onRetry={handleUploadUserResponses}/> :
                        isRecordingSync ?
                            <Card color={"primary"}
                                  sx={{
                                      overflowX: "hidden",
                                      alignItems: "center",
                                      alignSelf: "center",
                                      minWidth: {xs: "16rem", sm: "18rem", md: "25rem"},
                                      transition: "box-shadow 0.1s ease",
                                      boxShadow: `0 0 ${intensity * 55}px ${intensity * 24}px ${theme.palette['primary']['600']}`,
                                      gap: 1,
                                      borderRadius: 15,
                                      padding: 0,
                                      paddingTop: 3
                                  }}>
                                <User2 style={{fontSize: 120}}/>

                                <Stack alignItems={"center"} gap={1} padding={4}>
                                    <Typography level={"h2"}>
                                        {t("ieltsSpeakingCandidateLabel")}
                                    </Typography>

                                    <Chip variant={"outlined"}
                                          startDecorator={
                                              <motion.div
                                                  initial={{opacity: 0}}
                                                  animate={{opacity: [0, 1, 0]}}
                                                  transition={{
                                                      duration: 1,
                                                      repeat: Infinity,
                                                      ease: "linear"
                                                  }}
                                                  style={{
                                                      justifyContent: "center",
                                                      alignItems: "center",
                                                      display: "flex"
                                                  }}>

                                                  <Circle
                                                      style={{fill: theme.palette.primary["600"], color: "inherit"}}/>

                                              </motion.div>}
                                          color={"primary"} size={"lg"}
                                          sx={{paddingY: 1}}>
                                        {t("ieltsSpeakingRecordingLabel", {seconds: formatTime(recordingTime)})}
                                    </Chip>


                                </Stack>

                                <LinearProgress sx={{width: "100%", borderRadius: 0}} determinate
                                                value={Number(maxAllowedTimeProgressValue)}/>
                            </Card>
                            :

                            <Stack gap={2} alignItems={"center"} sx={{backgroundColor: "transparent"}}>

                                <Card color={"secondary"} sx={{
                                    alignItems: "center",
                                    alignSelf: "center",
                                    minWidth: {xs: "16rem", sm: "18rem", md: "25rem"},
                                    gap: 1,
                                    borderRadius: 15,
                                    overflow: "hidden",
                                    padding: 5
                                }}>

                                    {part2Countdown > 0 &&
                                        <Typography level={"body-lg"} textColor={"secondary.200"} sx={{paddingTop: 2}}>
                                            {t("speakingPartTwoCountdownLabel", {seconds: part2Countdown})}
                                        </Typography>}

                                    <UserRound style={{fontSize: 120}}/>

                                    <Stack alignItems={"center"}>
                                        <Typography level={"h2"}>
                                            {t("ieltsSpeakingExaminerLabel")}
                                        </Typography>
                                        <Typography color={"secondary"}>
                                            {t("ieltsSpeakingExaminerActionLabel")}
                                        </Typography>
                                    </Stack>

                                </Card>

                            </Stack>}

            </motion.div>
        </AnimatePresence>
    </Stack>

}

export default IeltsSpeakingQuestionComponent